// Toast — 토스트 아이템 타입·컨텍스트·훅 정의.
// ToastProvider.tsx에서 실제 Provider를 제공한다.
// 기존 components/Toast.tsx의 단순 push(kind, message) API와 구분:
// 이 버전은 title + description + action(undo) + duration 을 지원한다.

import { createContext, useContext } from 'react';

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: number;
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** 자동 닫힘 시간 ms (기본 5000, 0이면 자동 닫힘 없음) */
  duration?: number;
  action?: ToastAction;
}

export type ToastInput = Omit<ToastItem, 'id'>;

export interface ToastContextValue {
  /** 토스트 추가. id를 반환한다. */
  push: (input: ToastInput) => number;
  /** 특정 id의 토스트 즉시 제거 */
  dismiss: (id: number) => void;
  /** 현재 표시 중인 아이템 목록 (ToastProvider 내부 렌더용) */
  items: ToastItem[];
}

export const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * useToast — push / dismiss API.
 * @example
 * const { push } = useToast();
 * push({ title: '그룹 삭제됨', action: { label: '되돌리기', onClick: undo }, duration: 5000 });
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
